import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import Logo from '../components/Logo'
import AuthHeader from '../components/AuthHeader'
import QDayCountdown from '../components/QDayCountdown'
import {
  challengeApi,
  resourceFormalApi,
  formalrvApi,
  type FRVLeaderboard,
  type ChallengePublicInfo,
  type GlobalProgrammerEntry,
  type GlobalHardwareEntry,
  type LanguageStatsEntry,
  type RFFramework,
  type RFStatus,
} from '../utils/api'

function LandingPage() {
  const [searchQuery, setSearchQuery] = useState('')

  // Challenge system
  const [challenges, setChallenges] = useState<ChallengePublicInfo[]>([])
  const [isLoadingChallenges, setIsLoadingChallenges] = useState(true)
  const [challengeProgrammers, setChallengeProgrammers] = useState<GlobalProgrammerEntry[]>([])
  const [challengeHardware, setChallengeHardware] = useState<GlobalHardwareEntry[]>([])
  const [langStats, setLangStats] = useState<LanguageStatsEntry[]>([])

  // Quantum Hype Index
  const [indexData, setIndexData] = useState<{date:string;close:number}[]>([])
  const [indexCompanies, setIndexCompanies] = useState<{ticker:string;name:string;weight:number;color:string}[]>([])
  const indexCanvasRef = useRef<HTMLCanvasElement>(null)

  // Resource Verification (resourceFormal Lean audit)
  const [rfStatus, setRfStatus] = useState<RFStatus | null>(null)
  const [frvBoard, setFrvBoard] = useState<FRVLeaderboard | null>(null)
  const [rfFramework, setRfFramework] = useState<RFFramework | null>(null)

  useEffect(() => {
    challengeApi.list({ limit: 20 })
      .then(r => setChallenges(r.challenges))
      .catch(() => {})
      .finally(() => setIsLoadingChallenges(false))

    challengeApi.getGlobalLeaderboard(10)
      .then(r => setChallengeProgrammers(r.leaderboard))
      .catch(() => {})

    challengeApi.getGlobalHardwareRanking(10)
      .then(r => setChallengeHardware(r.leaderboard))
      .catch(() => {})

    challengeApi.getLanguageStats()
      .then(r => setLangStats(r.languages.sort((a, b) => b.submission_count - a.submission_count)))
      .catch(() => {})

    // Fetch Quantum Hype Index
    fetch('/api/quantum-index/data?period=6mo')
      .then(r => r.json())
      .then(d => { setIndexData(d.index_data || []); setIndexCompanies(d.companies || []) })
      .catch(() => {})

    // Fetch Resource Verification data (Lean audit of FT Shor)
    resourceFormalApi.getStatus().then(setRfStatus).catch(() => {})
    formalrvApi.getLeaderboard().then(setFrvBoard).catch(() => {})
    resourceFormalApi.getFramework().then(setRfFramework).catch(() => {})
  }, [])

  // Draw Quantum Hype Index chart
  useEffect(() => {
    const canvas = indexCanvasRef.current
    if (!canvas || indexData.length < 2) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const dpr = window.devicePixelRatio || 1
    const w = canvas.clientWidth, h = canvas.clientHeight
    canvas.width = w * dpr; canvas.height = h * dpr
    ctx.scale(dpr, dpr)
    const pad = { top: 28, right: 16, bottom: 32, left: 50 }
    const cw = w - pad.left - pad.right, ch = h - pad.top - pad.bottom
    const prices = indexData.map(d => d.close)
    const minP = Math.min(...prices) * 0.98, maxP = Math.max(...prices) * 1.02
    const range = maxP - minP || 1
    ctx.fillStyle = '#0f172a'; ctx.fillRect(0, 0, w, h)
    // Grid
    ctx.strokeStyle = '#1e293b'; ctx.lineWidth = 1
    for (let i = 0; i <= 4; i++) {
      const y = pad.top + (ch * i) / 4
      ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(w - pad.right, y); ctx.stroke()
      ctx.fillStyle = '#64748b'; ctx.font = '10px system-ui'; ctx.textAlign = 'right'
      ctx.fillText((maxP - (range * i) / 4).toFixed(1), pad.left - 6, y + 3)
    }
    // Date labels
    const step = Math.max(1, Math.floor(indexData.length / 5))
    ctx.fillStyle = '#64748b'; ctx.font = '9px system-ui'; ctx.textAlign = 'center'
    for (let i = 0; i < indexData.length; i += step) {
      const x = pad.left + (cw * i) / (indexData.length - 1)
      ctx.fillText(indexData[i].date.slice(5), x, h - 8)
    }
    // Line
    ctx.strokeStyle = '#a78bfa'; ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.beginPath()
    for (let i = 0; i < indexData.length; i++) {
      const x = pad.left + (cw * i) / (indexData.length - 1)
      const y = pad.top + ch - ((indexData[i].close - minP) / range) * ch
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
    }
    ctx.stroke()
    // Fill
    ctx.lineTo(pad.left + cw, pad.top + ch); ctx.lineTo(pad.left, pad.top + ch); ctx.closePath()
    const grad = ctx.createLinearGradient(0, pad.top, 0, pad.top + ch)
    grad.addColorStop(0, '#a78bfa40'); grad.addColorStop(1, '#a78bfa05')
    ctx.fillStyle = grad; ctx.fill()
    // Labels
    const last = indexData[indexData.length - 1], first = indexData[0]
    const chg = ((last.close - first.close) / first.close * 100)
    ctx.fillStyle = '#f8fafc'; ctx.font = 'bold 12px system-ui'; ctx.textAlign = 'left'
    ctx.fillText('Quantum Hype Index (6mo)', pad.left + 8, pad.top + 16)
    ctx.fillStyle = chg >= 0 ? '#22c55e' : '#ef4444'; ctx.font = 'bold 12px system-ui'; ctx.textAlign = 'right'
    ctx.fillText(last.close.toFixed(1) + '  ' + (chg >= 0 ? '+' : '') + chg.toFixed(1) + '%', w - pad.right - 8, pad.top + 16)
  }, [indexData])

  // Search with debounce
  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q)
    if (q.trim().length >= 2) {
      challengeApi.search(q)
        .then(r => setChallenges(r.challenges))
        .catch(() => {})
    } else if (q.trim().length === 0) {
      challengeApi.list({ limit: 20 })
        .then(r => setChallenges(r.challenges))
        .catch(() => {})
    }
  }, [])

  // Merge programmer leaderboards: existing users + challenge participants

  return (
    <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg">
      {/* Header Bar */}
      <header className="bg-white/80 backdrop-blur border-b border-qcloud-border px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center">
          <Logo size="small" showText />
        </div>
        <div className="flex items-center gap-4">
          <Link to="/shor" className="text-sm font-semibold text-qcloud-primary hover:text-qcloud-secondary transition-colors">
            Shor Leaderboard
          </Link>
          <Link to="/resource-verification" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">
            Resource Verification
          </Link>
          <Link to="/arena" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors font-medium">Arena</Link>
          <Link to="/about" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">About</Link>
          <Link to="/people" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">People</Link>
          <Link to="/updates" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">
            Updates
          </Link>
          <AuthHeader />
        </div>
      </header>

      {/* Floating TOC — left side */}
      <nav className="hidden 2xl:block fixed left-6 top-1/2 -translate-y-1/2 z-30">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-qcloud-border/50 shadow-2xl p-5 w-48">
          <div className="text-xs font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent uppercase tracking-widest mb-4">Navigate</div>
          <div className="space-y-1">
            {[
              ['#hero', 'Mission', '🎯'],
              ['#resource-verification', 'Resource Verification', '🔬'],
              ['#arena', 'Challenge Arena', '⚡'],
              ['#talks', 'Featured Talks', '🎬'],
              ['#pubs', 'Publications', '📄'],
              ['#debate', 'Quantum Hype Debate', '🔥'],
              ['#hype-index', 'Hype Index', '📈'],
              ['#collab', 'Collaborations', '🤝'],
            ].map(([href, label, icon]) => (
              <a
                key={href}
                href={href}
                className="flex items-center gap-2.5 text-sm text-qcloud-muted hover:text-qcloud-primary hover:bg-gradient-to-r hover:from-qcloud-primary/5 hover:to-qcloud-secondary/5 rounded-lg px-3 py-2 transition-all font-medium"
              >
                <span className="text-base">{icon}</span>
                {label}
              </a>
            ))}
          </div>
        </div>
        {/* Decorative vertical line */}
        <div className="absolute -right-3 top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-qcloud-primary/30 to-transparent" />
      </nav>

      {/* 3-Column Layout: Left (Human+AI) | Center | Right (Hardware+Language) */}
      <div className="flex justify-center">
        {/* Left Sidebar: Human Programmer + AI Leaderboards */}
        <aside className="hidden xl:block w-72 flex-shrink-0 sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto p-4">
          {/* Human Programmer Leaderboard */}
          <div className="bg-white rounded-xl border border-qcloud-border overflow-hidden">
            <div className="px-4 py-3 border-b border-qcloud-border bg-gradient-to-r from-qcloud-primary/5 to-qcloud-secondary/5">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-qcloud-text text-sm flex items-center gap-1.5">
                  <span>👑</span> Programmer
                </h2>
                <Link to="/competition/leaderboard" className="text-xs text-qcloud-primary hover:text-qcloud-secondary">View All →</Link>
              </div>
            </div>
            <div className="p-3">
              <div className="text-[10px] text-qcloud-muted font-medium mb-1.5 uppercase tracking-wide">Points: 3/2/1 per challenge</div>
              {challengeProgrammers.length > 0 ? (
                <div className="space-y-1">
                  {challengeProgrammers.slice(0, 10).map((entry, index) => (
                    <div key={entry.participant_label + index} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-qcloud-bg/50 transition-colors">
                      <span className={`w-5 text-center font-bold text-xs ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-slate-400' : index === 2 ? 'text-orange-400' : 'text-qcloud-muted'}`}>
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : entry.rank}
                      </span>
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-qcloud-primary to-qcloud-secondary flex items-center justify-center text-white text-[10px] font-medium">
                        {(entry.display_name || entry.participant_label).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-qcloud-text text-xs truncate">{entry.display_name || entry.participant_label}</div>
                        <div className="text-[10px] text-qcloud-muted truncate">{(entry as any).affiliation || ''}{(entry as any).affiliation ? ' · ' : ''}{entry.challenges_solved} solved</div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-qcloud-primary text-sm">{Math.round(entry.total_score)}</div>
                        <div className="text-[9px] text-qcloud-muted">pts</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-qcloud-muted">
                  <p className="text-sm mb-1">No competitors yet</p>
                  <p className="text-xs">Be the first to submit!</p>
                </div>
              )}
            </div>
          </div>

          {/* AI Agent Leaderboard */}
          <div className="bg-white rounded-xl border border-qcloud-border overflow-hidden mt-4">
            <div className="px-4 py-3 border-b border-qcloud-border bg-gradient-to-r from-emerald-500/10 to-teal-500/10">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-qcloud-text text-sm flex items-center gap-1.5">
                  <span>🤖</span> AI Agents
                </h2>
                <span className="text-[10px] text-emerald-600 font-medium">Coming Soon</span>
              </div>
            </div>
            <div className="p-3">
              <div className="text-center py-6 text-qcloud-muted">
                <div className="text-2xl mb-2">🤖</div>
                <p className="text-xs mb-1">AI Agent leaderboard</p>
                <p className="text-[10px]">Autonomous quantum programming agents will compete here.</p>
              </div>
            </div>
          </div>
        </aside>

        {/* Center Content */}
        <main className="flex-1 min-w-0 max-w-4xl">
          {/* Hero Section */}
          <section id="hero" className="text-center pt-12 pb-6 px-4 scroll-mt-16">
            <div className="flex justify-center mb-2">
              <Logo size="large" />
            </div>
            <h1 className="text-5xl font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">
              QuantumArena
            </h1>
            <p className="text-lg text-qcloud-muted mt-3 max-w-2xl mx-auto">
              Competition of quantum programmers, languages, error correction codes, and hardware in the early fault-tolerant era
            </p>
            <div className="mt-6 inline-block bg-gradient-to-r from-qcloud-primary/10 to-qcloud-secondary/10 border border-qcloud-primary/30 rounded-2xl px-6 py-3">
              <p className="text-lg md:text-xl font-semibold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">
                How far are we from useful quantum applications?
              </p>
              <p className="text-xs text-qcloud-muted mt-1">Build, benchmark, and find out.</p>
            </div>

            {/* Workflow Diagram: 5-column grid layout */}
            <div className="mt-10 max-w-4xl mx-auto grid grid-cols-[auto_auto_auto_auto_auto] justify-center items-center gap-x-1 md:gap-x-2 px-4">

              {/* Col 1: Two inputs stacked */}
              <div className="flex flex-col items-center gap-6">
                {/* QEC Codes */}
                <div className="flex flex-col items-center gap-1">
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[9px] font-bold">Stim</span>
                  <div className="bg-white rounded-xl border-2 border-emerald-200 p-2 shadow-lg w-[110px]">
                    <svg viewBox="0 0 100 50" className="w-full">
                      {/* Surface code mini */}
                      {[5,20,35].map(x => [5,20,35].map(y => <rect key={`sf${x}${y}`} x={x} y={y} width="13" height="13" rx="1" fill={(x+y)%30===0?"#86efac":"#fca5a5"} opacity="0.6" />))}
                      {[5,20,35,50].map(x => [5,20,35,50].map(y => <circle key={`sd${x}${y}`} cx={x} cy={y} r="1.8" fill="#374151" />))}
                      <text x="25" y="48" textAnchor="middle" fontSize="5" fill="#059669" fontWeight="bold">Surface</text>
                      {/* LDPC mini */}
                      {[60,72,84].map(x => [10,25,40].map(y => <circle key={`lp${x}${y}`} cx={x} cy={y} r="3" fill="#6366f1" opacity="0.6" />))}
                      <line x1="60" y1="10" x2="84" y2="25" stroke="#6366f1" strokeWidth="0.6" opacity="0.4" />
                      <line x1="72" y1="10" x2="60" y2="40" stroke="#6366f1" strokeWidth="0.6" opacity="0.4" />
                      <line x1="84" y1="10" x2="72" y2="40" stroke="#6366f1" strokeWidth="0.6" opacity="0.4" />
                      <line x1="60" y1="25" x2="84" y2="40" stroke="#6366f1" strokeWidth="0.6" opacity="0.4" />
                      <text x="72" y="48" textAnchor="middle" fontSize="5" fill="#6366f1" fontWeight="bold">LDPC</text>
                    </svg>
                  </div>
                  <div className="text-[10px] font-semibold text-qcloud-text">QEC Codes</div>
                </div>
                {/* Quantum Circuits */}
                <div className="flex flex-col items-center gap-1">
                  <div className="flex gap-1">
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[9px] font-bold">Qiskit</span>
                    <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[9px] font-bold">Qrisp</span>
                  </div>
                  <div className="bg-white rounded-xl border-2 border-blue-200 p-2 shadow-lg w-[110px]">
                    <svg viewBox="0 0 100 60" className="w-full">
                      <line x1="5" y1="12" x2="95" y2="12" stroke="#3b82f6" strokeWidth="1.5" />
                      <line x1="5" y1="30" x2="95" y2="30" stroke="#3b82f6" strokeWidth="1.5" />
                      <line x1="5" y1="48" x2="95" y2="48" stroke="#3b82f6" strokeWidth="1.5" />
                      <rect x="20" y="5" width="13" height="14" rx="2" fill="#6366f1" />
                      <text x="23" y="15" fontSize="8" fill="white" fontWeight="bold">H</text>
                      <circle cx="48" cy="12" r="3.5" fill="#6366f1" />
                      <line x1="48" y1="16" x2="48" y2="30" stroke="#6366f1" strokeWidth="1.5" />
                      <circle cx="48" cy="30" r="4.5" fill="none" stroke="#6366f1" strokeWidth="1.5" />
                      <rect x="62" y="23" width="13" height="14" rx="2" fill="#8b5cf6" />
                      <text x="63" y="33" fontSize="7" fill="white" fontWeight="bold">Rz</text>
                      <circle cx="82" cy="30" r="3.5" fill="#6366f1" />
                      <line x1="82" y1="34" x2="82" y2="48" stroke="#6366f1" strokeWidth="1.5" />
                      <circle cx="82" cy="48" r="4.5" fill="none" stroke="#6366f1" strokeWidth="1.5" />
                    </svg>
                  </div>
                  <div className="text-[10px] font-semibold text-qcloud-text">Quantum Circuits</div>
                  <div className="flex flex-wrap justify-center gap-1">
                    {['Shor','Grover','HHL','VQE'].map(a => <span key={a} className="px-1 py-0.5 bg-blue-50 text-blue-600 rounded text-[7px] font-medium">{a}</span>)}
                  </div>
                </div>
              </div>

              {/* Col 2: Converging arrows */}
              <svg className="w-12 md:w-16 self-center" viewBox="0 0 60 200" fill="none">
                <path d="M0 45 C30 45 30 95 55 95" stroke="#059669" strokeWidth="2" strokeDasharray="4 3" />
                <path d="M50 89 L58 95 L50 101" fill="#059669" />
                <path d="M0 155 C30 155 30 105 55 105" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 3" />
                <path d="M50 99 L58 105 L50 111" fill="#3b82f6" />
              </svg>

              {/* Col 3: Center — Platform */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-qcloud-primary to-qcloud-secondary p-[3px] shadow-xl">
                  <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                    <Logo size="medium" />
                  </div>
                </div>
                <div className="text-sm font-bold text-qcloud-primary">QuantumArena</div>
              </div>

              {/* Col 4: Fanning arrows */}
              <svg className="w-12 md:w-16 self-center" viewBox="0 0 60 200" fill="none">
                <path d="M5 100 C30 100 30 40 55 40" stroke="#6366f1" strokeWidth="2" strokeDasharray="4 3" />
                <path d="M50 34 L58 40 L50 46" fill="#6366f1" />
                <path d="M5 100 L55 100" stroke="#d97706" strokeWidth="2" strokeDasharray="4 3" />
                <path d="M50 94 L58 100 L50 106" fill="#d97706" />
                <path d="M5 100 C30 100 30 160 55 160" stroke="#0d9488" strokeWidth="2" strokeDasharray="4 3" />
                <path d="M50 154 L58 160 L50 166" fill="#0d9488" />
              </svg>

              {/* Col 5: Three hardware types stacked vertically */}
              <div className="flex flex-col items-center gap-3">
                {/* Superconducting */}
                <div className="flex flex-col items-center gap-1">
                  <div className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-lg p-1.5 shadow-xl w-[80px]">
                    <svg viewBox="0 0 80 70" className="w-full">
                      <ellipse cx="40" cy="6" rx="28" ry="4" fill="#d4a574" stroke="#b8860b" strokeWidth="0.8" />
                      <rect x="12" y="4" width="56" height="4" fill="#c9975c" />
                      <ellipse cx="40" cy="8" rx="28" ry="4" fill="#d4a574" stroke="#b8860b" strokeWidth="0.8" />
                      {[20,32,48,60].map(x => <path key={x} d={`M${x} 8 Q${x-2} 16 ${x+1} 22`} stroke="#d4a574" strokeWidth="0.7" fill="none" />)}
                      <ellipse cx="40" cy="22" rx="22" ry="3" fill="#d4a574" stroke="#b8860b" strokeWidth="0.6" />
                      {[28,40,52].map(x => <path key={x} d={`M${x} 25 Q${x} 32 ${x} 38`} stroke="#c9975c" strokeWidth="0.5" fill="none" />)}
                      <ellipse cx="40" cy="38" rx="16" ry="2.5" fill="#d4a574" stroke="#b8860b" strokeWidth="0.6" />
                      <rect x="32" y="44" width="16" height="8" rx="2" fill="#1e1b4b" stroke="#6366f1" strokeWidth="0.6" />
                      <circle cx="40" cy="48" r="2" fill="#818cf8" opacity="0.8" />
                      <text x="40" y="62" textAnchor="middle" fontSize="5" fill="#9ca3af" fontWeight="bold">SC QPU</text>
                    </svg>
                  </div>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[8px] font-bold">IBM</span>
                </div>
                {/* Ion Trap */}
                <div className="flex flex-col items-center gap-1">
                  <div className="bg-gradient-to-b from-amber-50 to-amber-100 rounded-lg border-2 border-amber-200 p-1.5 shadow-lg w-[80px]">
                    <svg viewBox="0 0 80 60" className="w-full">
                      <rect x="8" y="12" width="64" height="36" rx="18" fill="none" stroke="#b45309" strokeWidth="2" />
                      <rect x="12" y="16" width="56" height="28" rx="14" fill="none" stroke="#d97706" strokeWidth="0.8" strokeDasharray="3 2" />
                      {[22,32,42,52,62].map((x,i) => <circle key={i} cx={x} cy="30" r="3.5" fill={i%2===0?'#ef4444':'#3b82f6'} />)}
                      <line x1="4" y1="5" x2="32" y2="30" stroke="#fbbf24" strokeWidth="0.8" opacity="0.5" />
                      <line x1="76" y1="5" x2="52" y2="30" stroke="#fbbf24" strokeWidth="0.8" opacity="0.5" />
                      <text x="40" y="56" textAnchor="middle" fontSize="5" fill="#92400e" fontWeight="bold">ION TRAP</text>
                    </svg>
                  </div>
                  <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-[8px] font-bold">IonQ</span>
                </div>
                {/* Neutral Atom */}
                <div className="flex flex-col items-center gap-1">
                  <div className="bg-gradient-to-b from-cyan-50 to-pink-50 rounded-lg border-2 border-cyan-200 p-1.5 shadow-lg w-[80px]">
                    <svg viewBox="0 0 80 60" className="w-full">
                      {[20,40,60].map(x => [15,30,45].map(y => (
                        <g key={`t${x}${y}`}>
                          <path d={`M${x-4} ${y-8} L${x} ${y} L${x+4} ${y-8}`} fill="#67e8f9" opacity="0.25" />
                          <path d={`M${x-4} ${y+8} L${x} ${y} L${x+4} ${y+8}`} fill="#f9a8d4" opacity="0.25" />
                          <circle cx={x} cy={y} r="3" fill="#10b981" />
                        </g>
                      )))}
                      <text x="40" y="58" textAnchor="middle" fontSize="5" fill="#0d9488" fontWeight="bold">NEUTRAL ATOM</text>
                    </svg>
                  </div>
                  <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-[8px] font-bold">QuEra</span>
                </div>
              </div>
            </div>
          </section>

          {/* Search Bar */}
          <section className="max-w-2xl mx-auto px-4 mb-12">
            <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-qcloud-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearch(e.target.value)}
                placeholder="Search applications by name, category, or keyword..."
                className="w-full pl-12 pr-4 py-4 bg-white border-2 border-qcloud-border rounded-2xl text-qcloud-text placeholder-qcloud-muted focus:outline-none focus:border-qcloud-primary shadow-sm text-lg"
              />
            </div>
          </section>

          {/* Q-Day countdown — sets the stakes for the Resource Verification work below */}
          {rfStatus?.available && (
            <section className="max-w-6xl mx-auto px-4 mb-6">
              <QDayCountdown variant="full" showCTA />
            </section>
          )}

          {/* Resource Verification — Lean audit of FT Shor's algorithm */}
          {rfStatus?.available && (
            <ResourceVerificationHero
              board={frvBoard}
              framework={rfFramework}
            />
          )}

          {/* Challenge Arena */}
          <section id="arena" className="px-4 mb-16 scroll-mt-16">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-qcloud-text">Challenge Arena</h2>
              <Link to="/arena" className="text-sm text-qcloud-primary hover:underline">View All Tracks &rarr;</Link>
            </div>
            <p className="text-sm text-qcloud-muted mb-6 italic">
              Less hype, more qubits. Run your code on real hardware &mdash; fidelity speaks louder than words.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/arena/beginner"
                className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200 p-6 hover:border-green-400 hover:shadow-lg transition-all group"
              >
                <div className="text-3xl mb-3">{'\u26A1'}</div>
                <h3 className="text-lg font-bold text-green-700 mb-1 group-hover:text-green-800">Beginner Track</h3>
                <p className="text-sm text-qcloud-muted mb-3">Learn quantum gates, circuit identities, and hardware constraints.</p>
                <span className="text-xs text-green-600 font-medium">3 challenges &rarr;</span>
              </Link>
              <Link
                to="/arena/intermediate"
                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-6 hover:border-blue-400 hover:shadow-lg transition-all group"
              >
                <div className="text-3xl mb-3">{'\uD83D\uDD2C'}</div>
                <h3 className="text-lg font-bold text-blue-700 mb-1 group-hover:text-blue-800">Intermediate Track</h3>
                <p className="text-sm text-qcloud-muted mb-3">Implement real quantum algorithms like Grover and Deutsch-Jozsa.</p>
                <span className="text-xs text-blue-600 font-medium">5 challenges &rarr;</span>
              </Link>
              <Link
                to="/arena/advanced"
                className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 p-6 hover:border-purple-400 hover:shadow-lg transition-all group"
              >
                <div className="text-3xl mb-3">{'\uD83E\uDDEC'}</div>
                <h3 className="text-lg font-bold text-purple-700 mb-1 group-hover:text-purple-800">Advanced Track</h3>
                <p className="text-sm text-qcloud-muted mb-3">Fault-tolerant QEC challenges with research-grade benchmarks.</p>
                <span className="text-xs text-purple-600 font-medium">1 challenge &rarr;</span>
              </Link>
            </div>
          </section>

          {/* Featured Talks by Organizers */}
          <section id="talks" className="px-4 mb-16 scroll-mt-16">
            <h2 className="text-2xl font-bold text-qcloud-text mb-6">Featured Talks by Organizers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-qcloud-border overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/nHnlMiOaRXk"
                    title="[POPL'25] Invited Talk: Welcome to Quantum 3.0!"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-qcloud-text text-sm">[POPL'25] Welcome to Quantum 3.0!</h3>
                  <p className="text-xs text-qcloud-muted mt-1">CS challenges in early fault-tolerant quantum computing</p>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-qcloud-border overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video">
                  <iframe
                    className="w-full h-full"
                    src="https://www.youtube.com/embed/xhXddMYYgmQ"
                    title="Quantum education at scale - Quantum Summer Symposium 2021"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-qcloud-text text-sm">Quantum Education at Scale</h3>
                  <p className="text-xs text-qcloud-muted mt-1">Quantum Summer Symposium 2021</p>
                </div>
              </div>
            </div>
          </section>

          {/* Publications */}
          <section id="pubs" className="px-4 mb-16 scroll-mt-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-qcloud-text">Publications</h2>
              <Link to="/publications" className="text-sm text-qcloud-primary hover:underline">View All &rarr;</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href="https://arxiv.org/abs/2601.20247"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl border border-qcloud-border p-5 hover:border-qcloud-primary/50 hover:shadow-md transition-all group"
              >
                <h3 className="font-semibold text-qcloud-text group-hover:text-qcloud-primary text-sm leading-snug">
                  Computer Science Challenges in Quantum Computing: Early Fault-Tolerance and Beyond
                </h3>
                <p className="text-xs text-qcloud-muted mt-1">Yongshan Ding, Frederic T. Chong</p>
                <p className="text-xs text-qcloud-primary/70 font-medium mt-0.5">ACM Computing Surveys, 2024</p>
                <div className="flex gap-1.5 mt-2">
                  {['fault-tolerance', 'compilation', 'survey'].map(t => (
                    <span key={t} className="px-1.5 py-0.5 bg-qcloud-bg rounded text-[10px] text-qcloud-muted">{t}</span>
                  ))}
                </div>
              </a>
              <a
                href="https://iopscience.iop.org/article/10.1088/2058-9565/abfa64"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl border border-qcloud-border p-5 hover:border-qcloud-primary/50 hover:shadow-md transition-all group"
              >
                <h3 className="font-semibold text-qcloud-text group-hover:text-qcloud-primary text-sm leading-snug">
                  Achieving a Quantum Smart Workforce
                </h3>
                <p className="text-xs text-qcloud-muted mt-1">Clarice D. Aiello et al.</p>
                <p className="text-xs text-qcloud-primary/70 font-medium mt-0.5">Quantum Science and Technology, IOP, 2021</p>
                <div className="flex gap-1.5 mt-2">
                  {['education', 'workforce', 'quantum'].map(t => (
                    <span key={t} className="px-1.5 py-0.5 bg-qcloud-bg rounded text-[10px] text-qcloud-muted">{t}</span>
                  ))}
                </div>
              </a>
            </div>
          </section>

          {/* Quantum Hype Videos */}
          <section id="debate" className="px-4 mb-16 scroll-mt-16">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-qcloud-text">The Quantum Hype Debate</h2>
              <Link to="/quantum-index" className="text-sm text-qcloud-primary hover:underline">Quantum Hype Index 📈 &rarr;</Link>
            </div>
            <p className="text-sm text-qcloud-muted mb-6">
              Skeptics question it. Believers champion it. We say: run your algorithm on real hardware and let the results decide.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-qcloud-border overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video">
                  <iframe className="w-full h-full" src="https://www.youtube.com/embed/hOYwGttVRDQ" title="Quantum Hype 1" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              </div>
              <div className="bg-white rounded-xl border border-qcloud-border overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video">
                  <iframe className="w-full h-full" src="https://www.youtube.com/embed/gBTS7JZTyZY" title="Quantum Hype 2" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              </div>
              <div className="bg-white rounded-xl border border-qcloud-border overflow-hidden hover:shadow-lg transition-shadow">
                <div className="aspect-video">
                  <iframe className="w-full h-full" src="https://www.youtube.com/embed/pDj1QhPOVBo" title="Quantum Hype 3" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              </div>
            </div>
          </section>

          {/* Quantum Hype Index */}
          <section id="hype-index" className="px-4 mb-16 scroll-mt-16">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold text-qcloud-text">Quantum Hype Index</h2>
              <Link to="/quantum-index" className="text-sm text-qcloud-primary hover:underline">Full Page &rarr;</Link>
            </div>
            <p className="text-sm text-qcloud-muted mb-4">
              Real-time weighted index of quantum computing stocks. Each stock is normalized to 100 at the start of the period, then weighted and averaged.
            </p>
            {indexData.length > 0 ? (
              <>
                <div className="rounded-2xl overflow-hidden border border-slate-200 mb-4">
                  <canvas ref={indexCanvasRef} className="w-full" style={{ height: 260 }} />
                </div>
                {/* Company weights */}
                <div className="bg-white rounded-xl border border-qcloud-border p-4">
                  <h3 className="text-xs font-bold text-qcloud-muted uppercase tracking-wide mb-3">Index Composition</h3>
                  <p className="text-xs text-qcloud-muted mb-3">
                    Index = weighted average of (stock price / start price) &times; 100. A value above 100 means quantum stocks are collectively up from 6 months ago.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {indexCompanies.map(c => (
                      <div key={c.ticker} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-qcloud-text">{c.ticker}</div>
                          <div className="text-[10px] text-qcloud-muted truncate">{c.name} ({(c.weight * 100).toFixed(0)}%)</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl border border-qcloud-border p-8 text-center text-qcloud-muted text-sm">
                Loading index data...
              </div>
            )}
          </section>

          {/* Search Results (inline, only when searching) */}
          {searchQuery && (
          <section className="px-4 mb-16">
            <h2 className="text-lg font-bold text-qcloud-text mb-4">
              Results for "{searchQuery}"
            </h2>
            {isLoadingChallenges ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-3 border-qcloud-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : challenges.length === 0 ? (
              <div className="bg-white rounded-xl border border-qcloud-border p-8 text-center">
                <p className="text-qcloud-muted">No challenges match your search.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {challenges.map(c => (
                  <Link
                    key={c.id}
                    to={`/challenge/${c.id}`}
                    className="bg-white rounded-xl border border-qcloud-border p-4 hover:border-qcloud-primary/50 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        c.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                        c.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {c.difficulty}
                      </span>
                      {c.category && (
                        <span className="text-xs text-qcloud-muted">{c.category}</span>
                      )}
                    </div>
                    <h3 className="font-semibold text-qcloud-text group-hover:text-qcloud-primary text-sm mb-1">{c.title}</h3>
                    {c.description && (
                      <p className="text-xs text-qcloud-muted line-clamp-2">{c.description
                        .replace(/^#{1,3}\s+/gm, '')
                        .replace(/\*\*([^*]+)\*\*/g, '$1')
                        .replace(/`([^`]+)`/g, '$1')
                        .replace(/^[-*]\s+/gm, '')
                        .replace(/\n+/g, ' ')
                        .trim()}</p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </section>
          )}


        </main>

        {/* Right Sidebar: Hardware + Language Leaderboards */}
        <aside className="hidden xl:block w-72 flex-shrink-0 sticky top-16 self-start max-h-[calc(100vh-4rem)] overflow-y-auto p-4">
          {/* Hardware Leaderboard */}
          <div className="bg-white rounded-xl border border-qcloud-border overflow-hidden">
            <div className="px-4 py-3 border-b border-qcloud-border bg-gradient-to-r from-purple-500/10 to-indigo-500/10">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-qcloud-text text-sm flex items-center gap-1.5">
                  <span>⚛️</span> Hardware
                </h2>
                <Link to="/hardware/leaderboard" className="text-xs text-purple-600 hover:text-purple-700">View All →</Link>
              </div>
            </div>
            <div className="p-3">
              {challengeHardware.length > 0 ? (
                <div className="space-y-1">
                  {challengeHardware.map((entry, index) => (
                    <div key={entry.backend_name} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-purple-50/50 transition-colors">
                      <span className={`w-5 text-center font-bold text-xs ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-slate-400' : index === 2 ? 'text-orange-400' : 'text-qcloud-muted'}`}>
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : entry.rank}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-qcloud-text text-xs truncate">{entry.backend_name}</div>
                        <div className="text-[10px] text-qcloud-muted">{entry.total_jobs} jobs</div>
                      </div>
                      <div className="font-bold text-purple-600 text-xs">{(entry.avg_score * 100).toFixed(1)}%</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-qcloud-muted">
                  <p className="text-sm mb-1">No hardware data yet</p>
                  <p className="text-xs">Submit to real quantum hardware to see rankings!</p>
                </div>
              )}
            </div>
          </div>

          {/* Language Leaderboard */}
          <div className="bg-white rounded-xl border border-qcloud-border overflow-hidden mt-4">
            <div className="px-4 py-3 border-b border-qcloud-border bg-gradient-to-r from-indigo-500/10 to-purple-500/10">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-qcloud-text text-sm flex items-center gap-1.5">
                  <span>{'</>'}</span> Languages
                </h2>
                <Link to="/arena" className="text-xs text-indigo-600 hover:text-indigo-700">Arena →</Link>
              </div>
            </div>
            <div className="p-3">
              {langStats.length > 0 ? (
                <div className="space-y-2">
                  {langStats.map((lang, index) => {
                    const maxSubs = Math.max(1, ...langStats.map(l => l.submission_count))
                    const pct = (lang.submission_count / maxSubs) * 100
                    return (
                      <div key={lang.language} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-indigo-50/50 transition-colors">
                        <span className={`w-5 text-center font-bold text-xs ${index === 0 ? 'text-yellow-500' : index === 1 ? 'text-slate-400' : 'text-qcloud-muted'}`}>
                          {index === 0 ? '🥇' : index === 1 ? '🥈' : index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="font-medium text-qcloud-text text-xs">{lang.display_name}</span>
                            <span className="text-[10px] text-qcloud-muted">{lang.submission_count}</span>
                          </div>
                          <div className="bg-gray-100 rounded-full h-1.5 overflow-hidden">
                            <div className="bg-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-qcloud-muted text-sm">No submissions yet</div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Collaborations */}
      <section id="collab" className="bg-white border-t border-qcloud-border py-12 scroll-mt-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Academic */}
            <div className="text-center">
              <h3 className="text-xs font-semibold text-qcloud-muted uppercase tracking-widest mb-6">Academic Organization</h3>
              <a href="https://www.ucla.edu" target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-80 transition-opacity">
                <svg viewBox="0 0 200 60" className="h-14 mx-auto">
                  <text x="100" y="45" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="48" fill="#2774AE" letterSpacing="-2">UCLA</text>
                </svg>
              </a>
            </div>
            {/* Industry */}
            <div className="text-center">
              <h3 className="text-xs font-semibold text-qcloud-muted uppercase tracking-widest mb-6">Industry Collaboration</h3>
              <a href="https://quantum.ibm.com" target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-80 transition-opacity">
                <svg viewBox="0 0 200 80" className="h-14 mx-auto">
                  {/* IBM 8-bar logo */}
                  <g fill="#1F70C1">
                    {/* I */}
                    <rect x="20" y="8" width="30" height="6" /><rect x="20" y="18" width="30" height="6" />
                    <rect x="26" y="28" width="18" height="6" /><rect x="26" y="38" width="18" height="6" />
                    <rect x="26" y="48" width="18" height="6" /><rect x="26" y="58" width="18" height="6" />
                    <rect x="20" y="68" width="30" height="6" />
                    {/* B */}
                    <rect x="58" y="8" width="40" height="6" /><rect x="58" y="18" width="40" height="6" />
                    <rect x="64" y="28" width="12" height="6" /><rect x="82" y="28" width="16" height="6" />
                    <rect x="64" y="38" width="30" height="6" />
                    <rect x="64" y="48" width="12" height="6" /><rect x="84" y="48" width="16" height="6" />
                    <rect x="58" y="58" width="44" height="6" /><rect x="58" y="68" width="40" height="6" />
                    {/* M */}
                    <rect x="110" y="8" width="14" height="6" /><rect x="142" y="8" width="14" height="6" />
                    <rect x="110" y="18" width="18" height="6" /><rect x="138" y="18" width="18" height="6" />
                    <rect x="116" y="28" width="14" height="6" /><rect x="136" y="28" width="14" height="6" />
                    <rect x="116" y="38" width="34" height="6" />
                    <rect x="116" y="48" width="10" height="6" /><rect x="132" y="48" width="6" height="6" /><rect x="140" y="48" width="10" height="6" />
                    <rect x="116" y="58" width="10" height="6" /><rect x="140" y="58" width="10" height="6" />
                    <rect x="110" y="68" width="18" height="6" /><rect x="138" y="68" width="18" height="6" />
                  </g>
                </svg>
              </a>
            </div>
            {/* Software */}
            <div className="text-center">
              <h3 className="text-xs font-semibold text-qcloud-muted uppercase tracking-widest mb-6">Software Collaboration</h3>
              <a href="https://www.qrisp.eu" target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-80 transition-opacity">
                <svg viewBox="0 0 200 60" className="h-14 mx-auto">
                  <text x="100" y="45" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="42" fill="#2B3A67" letterSpacing="2">QRISP</text>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-qcloud-border py-6 text-center text-sm text-qcloud-muted">
        QuantumArena — Quantum computing competition platform
      </footer>
    </div>
  )
}

// ============================================================================
// ResourceVerificationHero — landing-page band linking to /resource-verification.
// Simplified framework diagram (4 layers + SysLayer + Verifier) + top-5
// leaderboard preview.
// ============================================================================

const RV_STATUS_COLOR = {
  verified: '#10b981',
  scaffolded: '#f59e0b',
  axiom: '#ef4444',
  structural: '#6366f1',
}

function RVMiniStatusBar({ counts }: { counts: { verified: number; scaffolded: number; axiom: number; structural: number; total: number } }) {
  const total = counts.total || 1
  const segs = [counts.verified, counts.scaffolded, counts.axiom, counts.structural]
  const cols = [RV_STATUS_COLOR.verified, RV_STATUS_COLOR.scaffolded, RV_STATUS_COLOR.axiom, RV_STATUS_COLOR.structural]
  return (
    <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden flex">
      {segs.map((v, i) => v > 0 && <div key={i} style={{ width: `${(v / total) * 100}%`, backgroundColor: cols[i] }} />)}
    </div>
  )
}

function ResourceVerificationHero({
  board,
  framework,
}: {
  board: FRVLeaderboard | null
  framework: RFFramework | null
}) {
  const rows = board
    ? [...board.entries.filter(e => e.rank !== null).sort((a, b) => a.rank! - b.rank!), ...board.entries.filter(e => e.kind === 'baseline')]
    : []
  const fmtQ = (n: number) => n >= 1e6 ? `${Number((n / 1e6).toPrecision(3))}M` : `${Number((n / 1e3).toPrecision(3))}k`
  const fmtT = (d: number) => d < 1 ? `${Number((d * 24).toPrecision(3))} h` : d < 730 ? `${Number(d.toPrecision(3))} d` : `${Math.round(d / 365.25).toLocaleString()} yr`

  return (
    <section id="resource-verification" className="px-4 mb-16 scroll-mt-16">
      <div className="max-w-6xl mx-auto">
        {/* Compact header */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-indigo-700 bg-indigo-100 rounded-full px-2.5 py-0.5 mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
              Lean 4 audit
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-qcloud-text">Resource Verification of Fault-Tolerant Shor</h2>
            <p className="text-sm italic text-qcloud-muted mt-1">Semantic correctness first. Resources as a consequence.</p>
          </div>
          <Link
            to="/shor"
            className="text-sm font-semibold px-4 py-2 rounded-lg bg-gradient-to-r from-qcloud-primary to-qcloud-secondary text-white hover:opacity-90 shadow-sm"
          >
            Open leaderboard →
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Framework preview (left) */}
          <Link
            to="/resource-verification"
            className="lg:col-span-7 bg-white rounded-2xl border border-qcloud-border p-4 shadow-sm hover:border-qcloud-primary transition-colors"
          >
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-wider text-qcloud-muted">Framework architecture</div>
              <span className="text-[11px] text-qcloud-primary">View detail →</span>
            </div>
            {!framework ? (
              <div className="text-center text-qcloud-muted text-sm py-6">Loading…</div>
            ) : (
              <RVFrameworkPreview framework={framework} />
            )}
          </Link>

          {/* FormalRV Shor leaderboard preview (right) */}
          <div className="lg:col-span-5 bg-white rounded-2xl border border-qcloud-border overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-qcloud-border">
              <div className="flex items-baseline justify-between">
                <div className="text-xs font-bold uppercase tracking-wider text-qcloud-muted">FT Shor Leaderboard</div>
                <span className="text-[11px] text-qcloud-muted">claimed → checked by FormalRV</span>
              </div>
            </div>
            <div className="divide-y divide-slate-100">
              {!board && <div className="px-4 py-6 text-center text-sm text-qcloud-muted">Loading…</div>}
              {rows.map(e => (
                <Link key={e.id} to={`/shor/${e.id}`} className="block px-4 py-2.5 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-5 text-sm font-bold text-qcloud-text tabular-nums">{e.rank ?? '—'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-qcloud-text truncate">{e.short_name}</div>
                      <div className="text-[10.5px] text-qcloud-muted truncate">{e.platform}</div>
                    </div>
                    <div className="text-right text-[11px] tabular-nums">
                      {e.claim && <div className="text-qcloud-muted">{fmtQ(e.claim.physical_qubits)} · {fmtT(e.claim.runtime_days)}</div>}
                      {e.checked && <div className="font-semibold text-qcloud-text">{fmtQ(e.checked.peak_qubits)} · {fmtT(e.checked.runtime_days)}</div>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="px-4 py-2 border-t border-qcloud-border bg-slate-50 text-[10.5px] text-qcloud-muted flex items-center justify-between">
              <span>Grey: paper's claim · bold: checked upper bound</span>
              <Link to="/shor" className="text-qcloud-primary font-medium">Full leaderboard →</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const RV_LAYER_ACCENT = [
  'border-rose-200    bg-rose-50/60',
  'border-amber-200   bg-amber-50/60',
  'border-emerald-200 bg-emerald-50/60',
  'border-sky-200     bg-sky-50/60',
]

function RVFrameworkPreview({ framework }: { framework: RFFramework }) {
  return (
    <div className="grid grid-cols-12 gap-2">
      {/* 4-layer stack */}
      <div className="col-span-7 space-y-1.5">
        {framework.layers.map((L, i) => (
          <div key={L.id} className={`px-2.5 py-1.5 rounded-lg border ${RV_LAYER_ACCENT[i % 4]}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="font-semibold text-[12px] text-qcloud-text">{L.name}</div>
              <div className="text-[10px] font-mono text-qcloud-muted">
                {L.status_counts.verified}✓
                {L.status_counts.scaffolded > 0 && <span className="text-amber-600 ml-1">{L.status_counts.scaffolded}⚠</span>}
                {L.status_counts.axiom > 0 && <span className="text-rose-600 ml-1">{L.status_counts.axiom}!</span>}
              </div>
            </div>
            <RVMiniStatusBar counts={L.status_counts} />
          </div>
        ))}
      </div>

      {/* SysLayer chip */}
      <div className="col-span-3 rounded-lg border-2 border-indigo-200 bg-indigo-50/50 p-2">
        <div className="font-bold text-[11px] text-indigo-900 mb-1.5">SysLayer</div>
        <div className="grid grid-cols-1 gap-1">
          {framework.syslayer?.invariants.map(inv => (
            <div key={inv.id} className="text-[10px] font-semibold text-indigo-800 px-1.5 py-0.5 rounded bg-white border border-indigo-100">
              <span className="text-indigo-600 font-bold mr-1">{inv.id}</span>{inv.name}
            </div>
          ))}
        </div>
      </div>

      {/* Verifier chip */}
      <div className="col-span-2 rounded-lg border-2 border-purple-200 bg-purple-50/50 p-2">
        <div className="font-bold text-[11px] text-purple-900 mb-1.5">Verifier</div>
        <div className="space-y-0.5">
          {(framework.verifiers?.actors ?? []).map(a => (
            <div key={a.id} className="text-[10px] font-semibold text-purple-800 px-1.5 py-0.5 rounded bg-white border border-purple-100 truncate" title={a.name}>
              {a.name.replace(' verifier', '')}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default LandingPage
